import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAllHouseholdMembers } from '../hooks/useHousehold';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import { useActiveMember } from '../hooks/useActiveMember';
import { useAllChoreDefinitions } from '../hooks/useChoreDefinitions';
import { useAllRooms } from '../hooks/useRooms';
import { ChoreLength, ChorePriority, Recurrence, type ChoreDefinition } from '@chore-project/shared';
import { PRIORITY_META, LENGTH_META, LENGTH_COLOR, RECURRENCE_LABEL } from '../lib/chore-meta';
import { NoHouseholdEmpty } from '../components/EmptyState';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getChoresForDate(date: Date, defs: ChoreDefinition[], userId: string): ChoreDefinition[] {
  const dow = date.getDay();
  const dom = date.getDate();
  return defs.filter((def) => {
    const isDue =
      def.recurrence === Recurrence.DAILY ||
      (def.recurrence === Recurrence.WEEKLY && (def.daysOfWeek?.includes(dow) ?? false)) ||
      (def.recurrence === Recurrence.MONTHLY && (def.daysOfMonth?.includes(dom) ?? false));
    if (!isDue) return false;
    return !def.defaultAssigneeId || def.defaultAssigneeId === userId;
  });
}

const MAX_VISIBLE = 4;

export function CalendarPage() {
  const { user } = useAuth();
  // Scoped to the primary's households — or, while acting as a shared user, to that
  // user's single household (so the calendar doesn't span households they're not in).
  const { households } = useSelectedHousehold();
  const { activeMember } = useActiveMember(user?.sub ?? '');
  const householdIds = (households ?? []).map((h) => h.householdId);
  const firstHouseholdId = householdIds[0] ?? '';
  // Definitions span households, so members and rooms must too — otherwise
  // entries from a non-selected household lose their assignee name and room.
  const { data: defs } = useAllChoreDefinitions(householdIds);
  const { data: members } = useAllHouseholdMembers(householdIds);
  const { data: rooms } = useAllRooms(householdIds);

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState<number | null>(today.getDate());

  // Filter chores for the identity in play: the shared user when acting as one.
  const userId = activeMember?.userId ?? user?.sub ?? '';

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array(firstDayOfMonth).fill(null) as null[],
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    setSelectedDay(null);
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    setSelectedDay(null);
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const getMemberName = (assigneeId: string) => {
    const m = members.find((mem) => mem.userId === assigneeId);
    return m?.memberName || m?.email || 'Unknown';
  };

  const isToday = (day: number) =>
    day === today.getDate() &&
    viewMonth === today.getMonth() &&
    viewYear === today.getFullYear();

  const selectedChores =
    selectedDay !== null
      ? getChoresForDate(new Date(viewYear, viewMonth, selectedDay), defs, userId)
      : [];

  const roomMap = new Map(rooms.map((r) => [r.roomId, r.name]));

  function groupChoresByRoom(chores: ChoreDefinition[]): Array<{ roomLabel: string; items: ChoreDefinition[] }> {
    const groups = new Map<string, ChoreDefinition[]>();
    for (const c of chores) {
      const key = c.roomId && roomMap.has(c.roomId) ? c.roomId : '';
      const group = groups.get(key) ?? [];
      group.push(c);
      groups.set(key, group);
    }
    const result: Array<{ roomLabel: string; items: ChoreDefinition[] }> = [];
    const namedKeys = [...groups.keys()]
      .filter((k) => k !== '')
      .sort((a, b) => (roomMap.get(a) ?? '').localeCompare(roomMap.get(b) ?? ''));
    for (const key of namedKeys) {
      result.push({ roomLabel: roomMap.get(key)!, items: groups.get(key)! });
    }
    if (groups.has('')) result.push({ roomLabel: 'Other', items: groups.get('')! });
    return result;
  }

  if (!firstHouseholdId) {
    return (
      <div className="page">
        <NoHouseholdEmpty />
      </div>
    );
  }

  const renderChore = (c: ChoreDefinition) => {
    const priorityIcon = PRIORITY_META[c.priority ?? ChorePriority.SHOULD_DO];
    const lengthLabel = LENGTH_META[c.length ?? ChoreLength.MEDIUM].label;
    return (
      <div key={c.choreDefId} className="item-row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <ion-icon name={priorityIcon.icon} style={{ color: priorityIcon.color, fontSize: '0.875rem', flexShrink: 0 }} />
            <strong>{c.name}</strong>
          </div>
          {c.description && (
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
              {c.description}
            </div>
          )}
          <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
            <span className="badge badge-neutral">{RECURRENCE_LABEL[c.recurrence] ?? c.recurrence}</span>
            {c.defaultAssigneeId
              ? <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{getMemberName(c.defaultAssigneeId)}</span>
              : <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Shared pool</span>
            }
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.75rem', color: LENGTH_COLOR, fontWeight: 500, flexShrink: 0, marginTop: '0.125rem' }}>
          <ion-icon name="timer-outline" style={{ fontSize: '0.875rem' }} />
          {lengthLabel}
        </span>
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Calendar</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={prevMonth} aria-label="Previous month">‹</button>
          <span style={{ fontWeight: 600, minWidth: '9rem', textAlign: 'center' }}>
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={nextMonth} aria-label="Next month">›</button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="cal-grid">
        {/* Day-of-week headers */}
        {DAY_NAMES.map((d) => (
          <div key={d} className="cal-col-header">{d}</div>
        ))}

        {/* Day cells */}
        {cells.map((day, idx) => {
          const isLastRow = idx >= cells.length - 7;
          const isLastCol = (idx + 1) % 7 === 0;
          const borderRight = isLastCol ? 'none' : '1px solid var(--border)';
          const borderBottom = isLastRow ? 'none' : '1px solid var(--border)';

          if (day === null) {
            return (
              <div
                key={`pad-${idx}`}
                className="cal-cell-pad"
                style={{ borderRight, borderBottom }}
              />
            );
          }

          const date = new Date(viewYear, viewMonth, day);
          const chores = getChoresForDate(date, defs, userId);
          const assignedCount = chores.filter((c) => !!c.defaultAssigneeId).length;
          const sharedCount = chores.filter((c) => !c.defaultAssigneeId).length;
          const isSelected = selectedDay === day;
          const todayCell = isToday(day);

          return (
            <div
              key={day}
              className={`cal-cell${isSelected ? ' cal-cell-selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              aria-label={`${MONTH_NAMES[viewMonth]} ${day}, ${chores.length === 0 ? 'no chores' : `${chores.length} chore${chores.length === 1 ? '' : 's'}`}`}
              onClick={() => setSelectedDay(isSelected ? null : day)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedDay(isSelected ? null : day);
                }
              }}
              style={{ borderRight, borderBottom }}
            >
              {/* Day number */}
              <div className="cal-day-num">
                <span className={`cal-day-circle${todayCell ? ' cal-day-circle-today' : ''}`}>
                  {day}
                </span>
              </div>

              {/* Chore pills — visible on desktop */}
              {chores.length > 0 && chores.length <= MAX_VISIBLE
                ? chores.map((c) => (
                    <div
                      key={c.choreDefId}
                      className={`cal-pill ${c.defaultAssigneeId ? 'cal-pill-assigned' : 'cal-pill-shared'}`}
                      title={c.name}
                    >
                      {c.name}
                    </div>
                  ))
                : chores.length > MAX_VISIBLE
                ? <div className="cal-overflow">{chores.length} chores</div>
                : null}

              {/* Count chips — visible on mobile */}
              {chores.length > 0 && (
                <div className="cal-chips">
                  {assignedCount > 0 && <span className="cal-chip cal-chip-assigned">{assignedCount}</span>}
                  {sharedCount > 0 && <span className="cal-chip cal-chip-shared">{sharedCount}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{ display: 'inline-block', width: '0.75rem', height: '0.75rem', borderRadius: '0.2rem', background: 'var(--primary-light)' }} />
          Assigned to you
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{ display: 'inline-block', width: '0.75rem', height: '0.75rem', borderRadius: '0.2rem', background: 'var(--surface-alt)', border: '1px solid var(--border)' }} />
          Shared pool
        </span>
      </div>

      {/* Day summary */}
      {selectedDay !== null && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <p className="section-label">
            {MONTH_NAMES[viewMonth]} {selectedDay}
            {' — '}
            {selectedChores.length === 0
              ? 'No chores'
              : `${selectedChores.length} chore${selectedChores.length !== 1 ? 's' : ''}`}
          </p>
          {selectedChores.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              No chores scheduled for this day.
            </p>
          ) : rooms.length > 0 ? (
            groupChoresByRoom(selectedChores).map(({ roomLabel, items }, i) => (
              <div key={roomLabel}>
                <p className="section-label" style={{ marginTop: i === 0 ? '0.25rem' : '1rem' }}>
                  {roomLabel} — {items.length}
                </p>
                {items.map(renderChore)}
              </div>
            ))
          ) : (
            selectedChores.map(renderChore)
          )}
        </div>
      )}
    </div>
  );
}
